import React, { useState, useEffect } from 'react';
import { Shield, Bell, Globe, Eye, EyeOff, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const [preferences, setPreferences] = useState({
    timezone: 'America/New_York',
    chat_sound: true,
    compact_mode: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Central European (CET)' },
    { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' }
  ];

  // Fetch preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('hyperdriveToken');
      const response = await fetch(`${API_URL}/api/auth/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setSaving(true);
    
    try {
      const token = localStorage.getItem('hyperdriveToken');
      const response = await fetch(`${API_URL}/api/auth/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [key]: value })
      });
      
      if (response.ok) {
        showMessage('success', 'Preference saved');
      } else {
        showMessage('error', 'Failed to save preference');
      }
    } catch (error) {
      showMessage('error', 'Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const token = localStorage.getItem('hyperdriveToken');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });
      
      if (response.ok) {
        showMessage('success', 'Password changed successfully');
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to change password');
      }
    } catch (error) {
      showMessage('error', 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showMessage('error', 'Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('hyperdriveToken');
      const response = await fetch(`${API_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: deletePassword })
      });
      
      if (response.ok) {
        logout();
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to delete account');
      }
    } catch (error) {
      showMessage('error', 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) {
    return (
      <div className="page-content settings-page">
        <div className="settings-loading">
          <Loader2 className="spinner" size={24} />
          <span>Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content settings-page">
      <h1 className="page-title">Settings</h1>
      <p className="page-description">Manage your account and preferences</p>

      {message.text && (
        <div className={`settings-message ${message.type}`}>
          {message.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
          {message.text}
        </div>
      )}

      <div className="settings-grid">
        {/* Account Security */}
        <div className="settings-card-new">
          <div className="settings-header-section">
            <Shield size={20} className="section-icon-settings" />
            <h3>Account Security</h3>
          </div>
          
          <form onSubmit={handleChangePassword} className="password-form">
            <h4 className="subsection-title">Change Password</h4>
            
            <div className="form-group">
              <label>Current Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                >
                  {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label>New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label>Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="settings-btn primary"
              disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password}
            >
              {changingPassword ? <><Loader2 className="spinner" size={16} /> Changing...</> : 'Change Password'}
            </button>
          </form>

          <div className="danger-zone">
            <h4 className="subsection-title danger">Danger Zone</h4>
            <p className="danger-text">Once you delete your account, there is no going back. All your data will be permanently removed.</p>
            <button 
              className="settings-btn danger"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Account
            </button>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="settings-card-new">
          <div className="settings-header-section">
            <Bell size={20} className="section-icon-settings" />
            <h3>Notification Preferences</h3>
          </div>
          <div className="settings-items">
            <div className="setting-row">
              <div className="setting-info">
                <span className="setting-name">Chat Sound</span>
                <p className="setting-desc">Play a sound when you receive new messages in Community Chat</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={preferences.chat_sound}
                  onChange={(e) => updatePreference('chat_sound', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Timezone */}
        <div className="settings-card-new">
          <div className="settings-header-section">
            <Globe size={20} className="section-icon-settings" />
            <h3>Timezone</h3>
          </div>
          <div className="settings-items">
            <div className="setting-row timezone-row">
              <div className="setting-info">
                <span className="setting-name">Default Timezone</span>
                <p className="setting-desc">Used for displaying trade timestamps and session times</p>
              </div>
              <select
                className="timezone-select"
                value={preferences.timezone}
                onChange={(e) => updatePreference('timezone', e.target.value)}
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h3>Delete Account</h3>
            </div>
            <div className="delete-modal-body">
              <p>This action <strong>cannot be undone</strong>. This will permanently delete:</p>
              <ul>
                <li>Your account and profile</li>
                <li>All your trade journal entries</li>
                <li>Your course enrollments</li>
                <li>Your chat history</li>
              </ul>
              
              <div className="form-group">
                <label>Enter your password to confirm</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                />
              </div>
              
              <div className="form-group">
                <label>Type <strong>DELETE</strong> to confirm</label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                />
              </div>
            </div>
            <div className="delete-modal-actions">
              <button 
                className="settings-btn secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </button>
              <button 
                className="settings-btn danger"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'DELETE' || !deletePassword}
              >
                {deleting ? <><Loader2 className="spinner" size={16} /> Deleting...</> : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
